<template>
    <div class="tw-flex tw-flex-col tw-gap-6">
        <!-- Destaque: corte do silo + veredito -->
        <section :class="card" class="tw-relative tw-overflow-hidden tw-p-5 sm:tw-p-7 md:tw-p-9 tw-flex tw-flex-col lg:tw-flex-row tw-gap-8 lg:tw-gap-10 lg:tw-items-center">
            <div class="tw-absolute -tw-left-36 -tw-bottom-52 tw-w-[560px] tw-h-[560px] tw-rounded-full tw-pointer-events-none" :style="{ background: statusInfo.brilho }"></div>

            <figure v-if="corte" class="tw-relative tw-m-0 tw-flex tw-flex-col tw-items-center tw-gap-3 tw-flex-shrink-0 tw-mx-auto lg:tw-mx-0">
                <svg :viewBox="corte.viewBox" width="340" :height="corte.altura" role="img" :aria-label="`Vista lateral do ${analise.silo_descricao} com a temperatura de cada sensor`">
                    <defs>
                        <pattern :id="idHachura" width="0.05" height="0.05" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                            <rect width="0.05" height="0.05" :fill="corte.cores.corpo" />
                            <rect width="0.025" height="0.05" :fill="corte.cores.anel" />
                        </pattern>
                    </defs>
                    <polygon :points="corte.telhado" :fill="corte.cores.telhado" />
                    <rect :x="corte.corpo.x" :y="corte.corpo.y" :width="corte.corpo.w" :height="corte.corpo.h" :fill="corte.cores.corpo" />
                    <line v-for="(y, i) in corte.aneis" :key="'a' + i" :x1="corte.corpo.x" :x2="corte.corpo.x + corte.corpo.w" :y1="y" :y2="y" :stroke="corte.cores.anel" :stroke-width="corte.traco.anel" />
                    <path v-if="corte.grao" :d="corte.grao" fill="#d4a24c" fill-opacity="0.38" />
                    <path v-if="corte.superficie" :d="corte.superficie" fill="none" :stroke-width="corte.traco.superficie" stroke="#f2c86b" stroke-linecap="round" />
                    <line v-for="l in corte.linhas" :key="'l' + l.cabo" :x1="l.x" :x2="l.x" :y1="l.y1" :y2="l.y2" :stroke="corte.cores.cabo" :stroke-width="corte.traco.cabo" />
                    <circle v-for="d in corte.pontos" :key="d.key" :cx="d.x" :cy="d.y" :r="corte.raio"
                        :fill="d.off ? `url(#${idHachura})` : d.cor"
                        :stroke="d.destaque ? '#f97316' : corte.cores.corpo" :stroke-width="corte.raio * (d.destaque ? 0.55 : 0.28)">
                        <title>{{ d.titulo }}</title>
                    </circle>
                    <text v-for="l in corte.rotulos" :key="'t' + l.cabo" :x="l.x" :y="l.y" text-anchor="middle" :font-size="corte.fonte" font-family="Roboto Mono, monospace" :fill="dark ? '#9ca3af' : '#6b7280'">{{ l.cabo }}</text>
                </svg>
                <div v-if="fileirasArmazem" class="tw-w-[340px] tw-flex tw-flex-col tw-gap-2">
                    <div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
                        <button type="button" @click="trocarFileira(-1)" :disabled="indiceFileira === 0" aria-label="Fileira anterior"
                            class="tw-w-9 tw-h-9 tw-rounded-lg tw-flex tw-items-center tw-justify-center disabled:tw-opacity-30" :class="dark ? 'tw-bg-white/5 hover:tw-bg-white/10' : 'tw-bg-gray-100 hover:tw-bg-gray-200'">
                            <svg class="tw-w-4 tw-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
                        </button>
                        <p class="tw-text-sm tw-text-center">
                            <strong>Fileira {{ fileiraAtual.arco }}</strong> <span :class="muted">de {{ fileirasArmazem.length }} · corte transversal</span>
                            <span v-if="fileiraAtual.critica" class="tw-block tw-text-xs tw-text-orange-400">{{ fileiraAtual.critica }}</span>
                        </p>
                        <button type="button" @click="trocarFileira(1)" :disabled="indiceFileira === fileirasArmazem.length - 1" aria-label="Próxima fileira"
                            class="tw-w-9 tw-h-9 tw-rounded-lg tw-flex tw-items-center tw-justify-center disabled:tw-opacity-30" :class="dark ? 'tw-bg-white/5 hover:tw-bg-white/10' : 'tw-bg-gray-100 hover:tw-bg-gray-200'">
                            <svg class="tw-w-4 tw-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                    </div>
                    <div class="tw-flex tw-gap-[3px]" role="group" aria-label="Fileiras do armazém, coloridas pela temperatura máxima">
                        <button v-for="f in fileirasArmazem" :key="f.arco" type="button" @click="fileira = f.arco"
                            :aria-pressed="f.arco === fileiraAtual.arco" :title="f.titulo"
                            class="tw-flex-1 tw-h-6 tw-rounded-sm tw-transition"
                            :style="{ background: f.cor, boxShadow: f.arco === fileiraAtual.arco ? `0 0 0 2px ${dark ? '#1a1d21' : '#fff'}, 0 0 0 4px #4ade80` : (f.aquecendo ? 'inset 0 0 0 2px #f97316' : 'none') }"></button>
                    </div>
                    <p class="tw-text-[11px] tw-text-center" :class="muted">Fileiras ao longo do comprimento, pela temperatura máxima</p>
                </div>
                <figcaption class="tw-flex tw-flex-wrap tw-justify-center tw-gap-x-4 tw-gap-y-1.5 tw-text-xs" :class="muted">
                    <span class="tw-inline-flex tw-items-center tw-gap-1.5"><span class="tw-w-14 tw-h-2 tw-rounded" :style="{ background: gradienteTemp }"></span>frio · quente</span>
                    <span v-if="corte.grao" class="tw-inline-flex tw-items-center tw-gap-1.5"><span class="tw-w-3.5 tw-h-2.5 tw-rounded-sm" style="background: rgba(192,138,46,.45); box-shadow: inset 0 1px 0 #e3be68"></span>massa de grãos</span>
                    <span class="tw-inline-flex tw-items-center tw-gap-1.5"><span class="tw-w-2.5 tw-h-2.5 tw-rounded-full" :style="{ boxShadow: '0 0 0 2px #f97316' }"></span>aquecendo</span>
                </figcaption>
                <p v-if="!corte.grao" class="tw-text-xs tw-text-center tw-max-w-[320px]" :class="muted">Nenhum sensor detectado dentro da massa de grãos.</p>
            </figure>

            <div class="tw-relative tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-6">
                <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <span :class="statusInfo.solido" class="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-black tw-tracking-wider tw-uppercase">
                        <svg class="tw-w-4 tw-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="statusInfo.icone"/></svg>
                        {{ statusInfo.texto }}
                    </span>
                    <span class="tw-text-sm" :class="muted">
                        {{ analise.silo_descricao }} · {{ analise.produto }} · objetivo {{ analise.objetivo }} · leitura {{ analise.leitura_data }}<span v-if="isAdmin"> · {{ analise.model }}</span>
                    </span>
                </div>
                <h1 class="tw-text-2xl md:tw-text-[32px] tw-leading-tight tw-font-black tw-tracking-tight">{{ rel.diagnostico }}</h1>
                <div class="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3">
                    <div v-for="k in stats" :key="k.label" class="tw-rounded-2xl tw-px-4 tw-py-4" :class="dark ? 'tw-bg-black/30' : 'tw-bg-gray-100'">
                        <p class="tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-wider" :class="muted">{{ k.label }}</p>
                        <p class="tw-mt-2 tw-flex tw-items-baseline tw-gap-1">
                            <span class="tw-font-mono tw-text-2xl md:tw-text-3xl tw-font-bold" :class="k.cor">{{ k.valor }}</span>
                            <span v-if="k.unidade" class="tw-text-sm" :class="muted">{{ k.unidade }}</span>
                        </p>
                        <p class="tw-text-[13px] tw-mt-1" :class="muted">{{ k.dica }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- O que fazer agora -->
        <section class="tw-flex tw-flex-col tw-gap-4">
            <h2 class="tw-flex tw-items-center tw-gap-2.5 tw-text-xl tw-font-bold" :class="titulo">
                <svg class="tw-w-5 tw-h-5 tw-text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                O que fazer agora
            </h2>
            <div class="tw-grid tw-gap-4" style="grid-template-columns: repeat(auto-fit, minmax(260px, 1fr))">
                <article v-for="a in acoesView" :key="a.n" :class="card" class="tw-p-6 tw-flex tw-flex-col tw-gap-3" :style="{ boxShadow: `inset 0 3px 0 ${a.cor}` }">
                    <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
                        <span class="tw-font-mono tw-text-4xl tw-font-bold tw-leading-none" :style="{ color: a.cor }">{{ a.n }}</span>
                        <span class="tw-px-2.5 tw-py-1 tw-rounded-full tw-text-xs tw-font-bold tw-tracking-wide" :style="{ color: a.cor, background: a.fundo }">{{ a.tag }}</span>
                    </div>
                    <p class="tw-text-[17px] tw-leading-snug tw-font-bold">{{ a.acao }}</p>
                    <p class="tw-text-sm tw-leading-relaxed" :class="muted">{{ a.motivo }}</p>
                </article>
            </div>
        </section>

        <!-- Riscos -->
        <section class="tw-flex tw-flex-col tw-gap-4">
            <h2 class="tw-flex tw-items-center tw-gap-2.5 tw-text-xl tw-font-bold" :class="titulo">
                <svg class="tw-w-5 tw-h-5 tw-text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l8 3v6c0 4.5-3.4 8-8 9-4.6-1-8-4.5-8-9V6z"/></svg>
                Riscos ao grão
            </h2>
            <div class="tw-grid tw-gap-4" style="grid-template-columns: repeat(auto-fit, minmax(220px, 1fr))">
                <article v-for="r in riscosView" :key="r.chave" :class="card" class="tw-overflow-hidden tw-flex tw-flex-col">
                    <div class="tw-h-1.5" :style="{ background: r.cor }"></div>
                    <div class="tw-p-5 tw-flex tw-flex-col tw-gap-2.5">
                        <span class="tw-self-start tw-px-2.5 tw-py-1 tw-rounded-md tw-text-xs tw-font-black tw-tracking-wider" :style="{ color: r.cor, background: r.fundo }">{{ r.nivelTexto }}</span>
                        <p class="tw-text-base tw-font-bold tw-leading-snug">{{ r.nome }}</p>
                        <p class="tw-text-sm tw-leading-relaxed" :class="muted">{{ r.evidencia }}</p>
                        <p v-if="r.explicacao" class="tw-text-sm tw-leading-relaxed" :class="soft">{{ r.explicacao }}</p>
                    </div>
                </article>
            </div>
        </section>

        <!-- Plano de aeração -->
        <section :class="card" class="tw-p-5 sm:tw-p-7 md:tw-p-8 tw-flex tw-flex-col tw-gap-6">
            <div class="tw-flex tw-flex-wrap tw-gap-3 tw-items-end tw-justify-between">
                <div>
                    <h2 class="tw-flex tw-items-center tw-gap-2.5 tw-text-xl tw-font-bold" :class="titulo">
                        <svg class="tw-w-5 tw-h-5 tw-text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M9.6 4.6A2 2 0 1 1 11 8H2M12.6 19.4A2 2 0 1 0 14 16H2M17.5 8a2.5 2.5 0 1 1 2 4H2"/></svg>
                        Plano de aeração
                    </h2>
                    <p class="tw-text-sm tw-mt-1.5" :class="muted">Quando o ar externo estiver dentro da faixa verde, os motores ligam.</p>
                </div>
                <span v-if="aer.atual && aer.atual.programa" class="tw-text-sm" :class="muted">Programa atual: <strong :class="soft">{{ aer.atual.programa }}</strong></span>
            </div>

            <div class="tw-flex tw-flex-col lg:tw-flex-row tw-gap-8">
                <div class="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-6">
                    <div v-for="r in reguas" :key="r.nome" class="tw-flex tw-flex-col tw-gap-2.5">
                        <div class="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-x-4 tw-gap-y-2">
                            <span class="tw-text-[15px] tw-font-bold" :class="r.corTexto">{{ r.nome }}</span>
                            <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                                <span class="tw-inline-flex tw-items-center tw-gap-2 tw-pl-2 tw-pr-3 tw-py-1 tw-rounded-lg">
                                    <span class="tw-w-5 tw-h-3 tw-rounded-sm tw-bg-green-500"></span>
                                    <span class="tw-text-xs" :class="muted">Recomendado</span>
                                    <strong class="tw-font-mono tw-text-[15px]" :class="titulo">{{ r.rec }}</strong>
                                </span>
                                <span v-if="r.atual" class="tw-inline-flex tw-items-center tw-gap-2 tw-pl-2 tw-pr-3 tw-py-1 tw-rounded-lg">
                                    <span class="tw-w-5 tw-h-3 tw-rounded-sm" :style="{ background: hachuraAtual }"></span>
                                    <span class="tw-text-xs" :class="muted">Programa atual</span>
                                    <strong class="tw-font-mono tw-text-[15px]" :class="r.atualDifere ? 'tw-text-orange-400' : titulo">{{ r.atual }}</strong>
                                </span>
                                <span v-if="r.agora" class="tw-inline-flex tw-items-center tw-gap-2 tw-pl-2 tw-pr-3 tw-py-1 tw-rounded-lg">
                                    <span class="tw-w-[3px] tw-h-3.5 tw-rounded" :class="dark ? 'tw-bg-white' : 'tw-bg-gray-900'"></span>
                                    <span class="tw-text-xs" :class="muted">Ar agora</span>
                                    <strong class="tw-font-mono tw-text-[15px]" :class="titulo">{{ r.agora }}</strong>
                                </span>
                            </div>
                        </div>
                        <div ref="reguaBarra" class="tw-relative tw-h-10 tw-rounded-xl" :class="dark ? 'tw-bg-[#15171a]' : 'tw-bg-gray-100'">
                            <div v-if="r.atualEstilo" class="tw-absolute tw-top-1.5 tw-bottom-1.5 tw-rounded-md" :style="r.atualEstilo"></div>
                            <div class="tw-absolute tw-top-3 tw-bottom-3 tw-rounded tw-bg-green-500" :style="r.recEstilo"></div>
                            <div v-if="r.agoraEstilo" class="tw-absolute tw-inset-y-0 tw-w-[3px] tw-rounded-full tw-bg-white" :class="dark ? '' : 'tw-ring-1 tw-ring-black'" :style="r.agoraEstilo" title="Ar externo agora"></div>
                        </div>
                        <div class="tw-flex tw-justify-between tw-font-mono tw-text-xs tw-text-gray-500"><span>{{ r.min }}</span><span>{{ r.max }}</span></div>
                    </div>
                    <p class="tw-text-[15px] tw-leading-relaxed tw-pl-4 tw-border-l-[3px] tw-border-green-500" :class="soft">{{ aer.justificativa }}</p>
                </div>

                <div class="tw-w-full lg:tw-w-[360px] tw-flex-shrink-0 tw-flex tw-flex-col tw-gap-4">
                    <div v-if="aer.agora" class="tw-rounded-2xl tw-p-5 tw-flex tw-items-center tw-gap-4"
                        :class="aer.agora.aeraria ? 'tw-bg-green-500/10 tw-ring-1 tw-ring-green-500/35' : 'tw-bg-orange-500/10 tw-ring-1 tw-ring-orange-500/35'">
                        <div class="tw-w-14 tw-h-14 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-flex-shrink-0" :class="aer.agora.aeraria ? 'tw-bg-emerald-500' : 'tw-bg-orange-500'">
                            <i class="fa-solid fa-fan tw-text-white tw-text-2xl"></i>
                        </div>
                        <div>
                            <p class="tw-text-xs tw-font-bold tw-tracking-[0.1em]" :class="aer.agora.aeraria ? 'tw-text-green-400' : 'tw-text-orange-400'">AGORA</p>
                            <p class="tw-text-xl tw-font-black">{{ aer.agora.aeraria ? 'Pode aerar' : 'Não aerar' }}</p>
                            <p class="tw-text-sm" :class="soft">{{ fmt(aer.agora.temperatura) }} °C · {{ fmt(aer.agora.umidade, 0) }}% UR · {{ aer.agora.chuva ? 'com chuva' : 'sem chuva' }}</p>
                        </div>
                    </div>
                    <div v-if="horas" class="tw-rounded-2xl tw-p-5" :class="dark ? 'tw-bg-[#15171a]' : 'tw-bg-gray-100'">
                        <div class="tw-flex tw-justify-between tw-items-baseline">
                            <p class="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider" :class="muted">Horas viáveis em 7 dias</p>
                            <span class="tw-font-mono tw-text-2xl tw-font-bold tw-text-green-400">{{ horas.total }} h</span>
                        </div>
                        <div class="tw-grid tw-gap-2 tw-items-end tw-h-24 tw-mt-4" :style="{ gridTemplateColumns: `repeat(${horas.por_dia.length}, minmax(0, 1fr))` }">
                            <div v-for="d in horas.por_dia" :key="d.data" class="tw-flex tw-flex-col tw-items-center tw-justify-end tw-h-full tw-gap-1">
                                <span class="tw-font-mono tw-text-xs" :class="soft">{{ d.horas }}h</span>
                                <div class="tw-w-full tw-max-w-[36px] tw-rounded-t-md" :class="d.horas ? 'tw-bg-green-500' : (dark ? 'tw-bg-gray-700' : 'tw-bg-gray-300')" :style="{ height: barra(d.horas) }"></div>
                            </div>
                        </div>
                        <div class="tw-grid tw-gap-2 tw-mt-1.5" :style="{ gridTemplateColumns: `repeat(${horas.por_dia.length}, minmax(0, 1fr))` }">
                            <span v-for="d in horas.por_dia" :key="'d' + d.data" class="tw-text-center tw-text-xs" :class="muted">{{ d.data }}</span>
                        </div>
                        <p v-if="horas.atual_umidas" class="tw-text-[13px] tw-leading-relaxed tw-mt-3" :class="muted">
                            O programa atual teria ligado <strong class="tw-text-orange-400">{{ horas.atual_umidas }} h com ar acima de {{ rec.umidade_maxima }}% UR</strong> no mesmo período.
                        </p>
                    </div>
                    <p v-if="!aer.agora && !horas" class="tw-text-sm" :class="muted">Sem dados da estação meteorológica para avaliar as condições externas.</p>
                </div>
            </div>
        </section>
        <section :class="card" class="tw-p-5 sm:tw-p-7 md:tw-p-8 tw-flex tw-flex-col tw-gap-5">
            <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <div>
                    <h2 class="tw-flex tw-items-center tw-gap-2.5 tw-text-xl tw-font-bold" :class="titulo">
                        <svg class="tw-w-5 tw-h-5 tw-text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>
                        Mapa térmico
                    </h2>
                    <p class="tw-text-sm tw-mt-1.5 tw-max-w-3xl" :class="muted">
                        {{ explicacaoMassa }}<span v-if="!mapa.tem_historico"> Sem leitura de 7 dias atrás: não foi possível calcular a tendência.</span>
                    </p>
                </div>
                <div v-if="mapa.tem_historico" class="tw-inline-flex tw-rounded-xl tw-p-1" :class="dark ? 'tw-bg-[#15171a]' : 'tw-bg-gray-100'" role="group" aria-label="Modo do mapa">
                    <button type="button" @click="modoMapa = 'temp'" :aria-pressed="modoMapa === 'temp'" :class="abaClass('temp')" class="tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm">Temperatura</button>
                    <button type="button" @click="modoMapa = 'delta'" :aria-pressed="modoMapa === 'delta'" :class="abaClass('delta')" class="tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm">Variação 7 dias</button>
                </div>
            </div>

            <div v-if="massaTodaAcimaIdeal && modoMapa === 'temp'" class="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-px-4 tw-py-3 tw-bg-red-500/10 tw-ring-1 tw-ring-red-500/30 tw-text-sm tw-text-red-400">
                <svg class="tw-h-4 tw-w-4 tw-flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/></svg>
                Toda a massa está acima do máximo ideal de {{ ind.faixa_ideal.max }} °C. As cores mostram as diferenças entre os sensores.
            </div>

            <div v-if="mapa.cabos && mapa.cabos.length" class="tw-overflow-x-auto custom-scrollbar">
                <div class="tw-grid tw-gap-1.5" :style="{ gridTemplateColumns: `52px repeat(${mapa.cabos.length}, minmax(56px, 1fr))`, minWidth: `${52 + mapa.cabos.length * 62}px` }">
                    <span></span>
                    <span v-for="cabo in mapa.cabos" :key="'h' + cabo.cabo" class="tw-text-center tw-text-sm tw-font-bold tw-pb-1" :class="muted">C{{ cabo.cabo }}</span>
                    <template v-for="linha in linhasMapa" :key="linha.key">
                        <div v-if="linha.nivel" class="tw-flex tw-items-center tw-gap-3 tw-my-1 tw-text-xs tw-font-bold tw-tracking-wider tw-text-green-400" style="grid-column: 1 / -1">
                            <span class="tw-flex-1 tw-border-t-2 tw-border-dashed tw-border-green-400/70"></span>
                            {{ mapa.origem_nivel === 'manual' ? 'NÍVEL INFORMADO' : 'NÍVEL ESTIMADO' }} {{ fmt(ind.nivel, 0) }}%
                            <span class="tw-flex-1 tw-border-t-2 tw-border-dashed tw-border-green-400/70"></span>
                        </div>
                        <template v-else>
                            <span class="tw-font-mono tw-text-[13px] tw-self-center" :class="muted">S{{ linha.sensor }}</span>
                            <div v-for="cel in linha.celulas" :key="cel.key" :title="cel.titulo" :class="[cel.classe, alturaCelula]" :style="cel.estilo"
                                class="tw-relative tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-font-mono tw-text-sm tw-font-medium tw-tabular-nums">
                                {{ cel.texto }}
                                <span v-if="cel.topo" class="tw-absolute -tw-top-[5px] tw-left-0 tw-right-0 tw-border-t-2 tw-border-dashed tw-border-green-400 tw-pointer-events-none"></span>
                                <span v-if="cel.acimaIdeal" class="tw-absolute tw-top-1 tw-right-1 tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-600 tw-ring-1 tw-ring-white/70"></span>
                            </div>
                        </template>
                    </template>
                </div>
            </div>
            <p v-else class="tw-text-sm" :class="muted">Sem dados de sensores para montar o mapa.</p>
            <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-5 tw-gap-y-2 tw-text-[13px]" :class="muted">
                <span v-if="legendaEscala" class="tw-inline-flex tw-items-center tw-gap-2">
                    <span class="tw-font-mono">{{ legendaEscala.min }}</span>
                    <span class="tw-inline-block tw-w-28 tw-h-3 tw-rounded-sm" :style="{ background: legendaEscala.gradiente }"></span>
                    <span class="tw-font-mono">{{ legendaEscala.max }}</span>
                </span>
                <span v-for="l in legenda" :key="l.texto" class="tw-inline-flex tw-items-center tw-gap-2">
                    <span class="tw-relative tw-inline-block tw-w-4 tw-h-4 tw-rounded" :class="l.classe" :style="l.estilo"></span>{{ l.texto }}
                </span>
            </div>
        </section>
        <section v-if="manutencao.length" :class="card" class="tw-p-6 md:tw-p-7 tw-flex tw-flex-col tw-gap-3">
            <p class="tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-blue-400">Manutenção</p>
            <p class="tw-text-[15px]" :class="soft"><strong class="tw-font-mono tw-text-2xl" :class="titulo">{{ ind.cobertura.sensores_off }}</strong> de {{ ind.cobertura.sensores_total }} sensores sem leitura</p>
            <div class="tw-grid tw-gap-2" style="grid-template-columns: repeat(auto-fill, minmax(240px, 1fr))">
                <div v-for="m in manutencao" :key="m.cabo" class="tw-flex tw-justify-between tw-gap-3 tw-rounded-xl tw-px-4 tw-py-2.5 tw-text-sm" :class="dark ? 'tw-bg-[#15171a]' : 'tw-bg-gray-100'">
                    <span class="tw-font-bold">Cabo {{ m.cabo }}</span>
                    <span class="tw-text-right" :class="muted">Sensores {{ m.sensores_off.join(', ') }}</span>
                </div>
            </div>
        </section>

        <div v-if="isAdmin" class="tw-flex tw-justify-end">
            <button type="button" @click="$emit('excluir')" :disabled="isDeleting"
                class="tw-px-5 tw-py-2.5 tw-rounded-lg tw-border tw-border-red-500/50 tw-text-red-400 hover:tw-bg-red-500/10 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed tw-text-sm tw-font-bold tw-uppercase tw-tracking-wider">
                {{ isDeleting ? 'Excluindo...' : 'Excluir análise' }}
            </button>
        </div>
    </div>
</template>

<script>
import { geometriaCabos, geometriaArmazem, tetoArmazem, ARMAZEM, R, R_TELHADO, Y_BASE, Y_BEIRAL, TELHADO_H } from '@/utils/siloGeometria';

const ICONE_ALERTA = 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z';
const STATUS = {
    seguro: { texto: 'Seguro', solido: 'tw-bg-green-500 tw-text-green-950', icone: 'M5 12l5 5 9-10', brilho: 'radial-gradient(circle, rgba(34,197,94,.16), rgba(34,197,94,0) 65%)' },
    atencao: { texto: 'Atenção', solido: 'tw-bg-yellow-400 tw-text-yellow-950', icone: ICONE_ALERTA, brilho: 'radial-gradient(circle, rgba(234,179,8,.16), rgba(234,179,8,0) 65%)' },
    acao: { texto: 'Ação necessária', solido: 'tw-bg-orange-500 tw-text-orange-950', icone: ICONE_ALERTA, brilho: 'radial-gradient(circle, rgba(249,115,22,.18), rgba(249,115,22,0) 65%)' },
};

const CATEGORIAS = { aeracao: 'Aeração', inspecao: 'Inspeção', manutencao: 'Manutenção', acompanhamento: 'Acompanhamento' };
const PRIORIDADE = [
    { cor: '#f97316', fundo: 'rgba(249,115,22,.16)' },
    { cor: '#f97316', fundo: 'rgba(249,115,22,.16)' },
    { cor: '#facc15', fundo: 'rgba(234,179,8,.15)' },
    { cor: '#9ca3af', fundo: 'rgba(156,163,175,.12)' },
];

const NIVEIS = {
    alto: { texto: 'ALTO', cor: '#f97316', fundo: 'rgba(249,115,22,.18)' },
    medio: { texto: 'MÉDIO', cor: '#eab308', fundo: 'rgba(234,179,8,.15)' },
    baixo: { texto: 'BAIXO', cor: '#60a5fa', fundo: 'rgba(59,130,246,.15)' },
    indeterminado: { texto: 'SEM DADOS', cor: '#9ca3af', fundo: 'rgba(156,163,175,.12)' },
};

const ESCALA_TEMP = ['#1d4ed8', '#60a5fa', '#475569', '#fb923c', '#ea580c'];
const ESCALA_CORTE = ['#2563eb', '#60a5fa', '#e2e8f0', '#fdba74', '#f97316'];
const ESCALA_CORTE_CLARO = ['#1d4ed8', '#3b82f6', '#64748b', '#f97316', '#c2410c'];
const ESCALA_DELTA = ['#334155', '#a16207', '#fb923c', '#f97316'];

function hexRgb(h) {
    const n = parseInt(h.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function corEscala(paradas, t) {
    const x = Math.min(1, Math.max(0, isFinite(t) ? t : 0.5)) * (paradas.length - 1);
    const i = Math.min(paradas.length - 2, Math.floor(x));
    const f = x - i;
    const a = hexRgb(paradas[i]);
    const b = hexRgb(paradas[i + 1]);
    const rgb = a.map((v, k) => Math.round(v + (b[k] - v) * f));
    const lum = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
    return { background: `rgb(${rgb.join(',')})`, color: lum > 0.5 ? '#111827' : '#ffffff' };
}

function caminhoSuave(pts) {
    const p = pts.map(([x, y]) => [x, -y]);
    let d = `M ${p[0][0].toFixed(3)} ${p[0][1].toFixed(3)}`;
    for (let i = 1; i < p.length - 1; i++) {
        const mx = (p[i][0] + p[i + 1][0]) / 2;
        const my = (p[i][1] + p[i + 1][1]) / 2;
        d += ` Q ${p[i][0].toFixed(3)} ${p[i][1].toFixed(3)} ${mx.toFixed(3)} ${my.toFixed(3)}`;
    }
    const u = p[p.length - 1];
    return `${d} L ${u[0].toFixed(3)} ${u[1].toFixed(3)}`;
}

function gradienteCss(paradas) {
    return `linear-gradient(90deg, ${paradas.join(', ')})`;
}

export default {
    name: 'RelatorioAnaliseIA',
    props: {
        analise: { type: Object, required: true },
        theme: { type: String, default: 'dark' },
        isAdmin: { type: Boolean, default: false },
        isDeleting: { type: Boolean, default: false },
    },
    emits: ['excluir'],
    data() {
        return { modoMapa: 'temp', larguraRegua: 0, fileira: null };
    },
    computed: {
        dark() { return this.theme === 'dark'; },
        card() { return this.dark ? 'tw-bg-[#1a1d21] tw-text-gray-100 tw-rounded-2xl tw-shadow-2xl' : 'tw-bg-white tw-text-gray-800 tw-rounded-2xl tw-shadow'; },
        muted() { return this.dark ? 'tw-text-gray-400' : 'tw-text-gray-600'; },
        soft() { return this.dark ? 'tw-text-gray-300' : 'tw-text-gray-700'; },
        titulo() { return this.dark ? 'tw-text-gray-100' : 'tw-text-gray-900'; },
        escalaCorte() { return this.dark ? ESCALA_CORTE : ESCALA_CORTE_CLARO; },
        gradienteTemp() { return gradienteCss(this.escalaCorte); },
        hachuraAtual() { return 'repeating-linear-gradient(135deg, #4b5563 0 4px, #374151 4px 8px)'; },

        rel() { return this.analise.response || {}; },
        ind() { return this.rel.indicadores || {}; },
        mapa() { return this.rel.mapa || { cabos: [] }; },
        aer() { return this.rel.aeracao || {}; },
        rec() { return this.aer.recomendado || {}; },
        horas() {
            const h = this.aer.horas_viaveis;
            return h && h.por_dia && h.por_dia.length ? h : null;
        },
        statusInfo() { return STATUS[this.rel.status] || STATUS.atencao; },
        manutencao() {
            return ((this.rel.manutencao && this.rel.manutencao.cabos_com_defeito) || [])
                .filter(c => c.sensores_off && c.sensores_off.length)
                .slice()
                .sort((a, b) => b.sensores_off.length - a.sensores_off.length);
        },

        stats() {
            const i = this.ind;
            const faixa = i.faixa_ideal || {};
            const quente = i.ponto_mais_quente;
            const aquec = i.maior_aquecimento;
            const neutro = this.dark ? 'tw-text-gray-100' : 'tw-text-gray-900';
            const lista = [];
            if (i.silo_vazio) {
                lista.push({ label: 'Ambiente interno', valor: this.fmt(i.media_ambiente), unidade: '°C', cor: neutro, dica: 'silo sem grãos na massa' });
            } else {
                lista.push({
                    label: 'Média da massa', valor: this.fmt(i.media), unidade: '°C',
                    cor: faixa.max != null && i.media > faixa.max ? 'tw-text-orange-400' : 'tw-text-green-400',
                    dica: faixa.max != null ? `faixa ideal ${faixa.min}–${faixa.max} °C` : '',
                });
            }
            lista.push({
                label: 'Ponto mais quente', valor: quente ? this.fmt(quente.temperatura) : '—', unidade: quente ? '°C' : '',
                cor: 'tw-text-orange-400', dica: quente ? `Cabo ${quente.cabo} · Sensor ${quente.sensor}` : 'sem sensores na massa',
            });
            if (aquec) {
                lista.push({ label: 'Em 7 dias', valor: this.sinal(aquec.delta), unidade: '°C', cor: 'tw-text-yellow-400', dica: `maior alta, Cabo ${aquec.cabo} · S${aquec.sensor}` });
            } else {
                lista.push({
                    label: 'Em 7 dias', valor: i.variacao_7d != null ? this.sinal(i.variacao_7d) : '—', unidade: i.variacao_7d != null ? '°C' : '',
                    cor: neutro, dica: i.variacao_7d != null ? 'média da massa' : 'sem leitura de referência',
                });
            }
            lista.push(i.ug != null
                ? { label: 'Umidade do grão', valor: this.fmt(i.ug), unidade: '%', cor: i.ug > i.ug_meta ? 'tw-text-yellow-400' : 'tw-text-green-400', dica: `segura: ${this.fmt(i.ug_meta)}%` }
                : { label: 'Umidade do grão', valor: '—', unidade: '', cor: this.muted, dica: 'não informada' });
            return lista;
        },

        // Armazém: fileiras (arcos) ao longo do comprimento, com a temperatura máxima de
        // cada uma para o seletor. Null para silos.
        fileirasArmazem() {
            if (this.analise.silo_tipo !== 'armazem') return null;
            const config = this.analise.cabos_config || [];
            const porCabo = Object.fromEntries((this.mapa.cabos || []).map(c => [String(c.cabo), c]));
            if (!config.length || config.some(c => !porCabo[String(c.cabo)])) return null;
            const d = this.dominioTemp;
            return [...new Set(config.map(c => c.arco))].sort((a, b) => a - b).map(arco => {
                const sensores = config.filter(c => c.arco === arco).flatMap(c => porCabo[String(c.cabo)].sensores);
                const temps = sensores.filter(x => !x.off && x.temperatura != null).map(x => x.temperatura);
                const max = temps.length ? Math.max(...temps) : null;
                const aquecendo = sensores.some(x => x.foco || x.aquecendo);
                return {
                    arco, max, aquecendo,
                    cor: max == null ? (this.dark ? '#2f353d' : '#e5e7eb') : corEscala(this.escalaCorte, d ? (max - d.min) / (d.max - d.min) : 0.5).background,
                    titulo: `Fileira ${arco}` + (max != null ? ` · máx. ${this.fmt(max)} °C` : ' · sem leitura') + (aquecendo ? ' · aquecendo' : ''),
                };
            });
        },

        // Fileira mostrada: a escolhida, ou a mais crítica (aquecendo, depois a mais quente)
        fileiraAtual() {
            const lista = this.fileirasArmazem;
            if (!lista) return null;
            const escolhida = lista.find(f => f.arco === this.fileira);
            const critica = lista.slice().sort((a, b) => (b.aquecendo - a.aquecendo) || ((b.max ?? -Infinity) - (a.max ?? -Infinity)))[0];
            const atual = escolhida || critica;
            let motivo = '';
            if (atual === critica && lista.length > 1) motivo = critica.aquecendo ? 'fileira com aquecimento' : 'fileira mais quente';
            return { ...atual, critica: motivo };
        },

        indiceFileira() {
            return this.fileirasArmazem ? this.fileirasArmazem.findIndex(f => f.arco === this.fileiraAtual.arco) : -1;
        },

        idHachura() { return `ia-off-${this.analise.id}`; },
        corte() {
            const cabosMapa = this.mapa.cabos || [];
            if (!cabosMapa.length) return null;
            const porCabo = Object.fromEntries(cabosMapa.map(c => [String(c.cabo), c]));
            const config = (this.analise.cabos_config || []).filter(c => porCabo[String(c.cabo)]);
            const tipo = this.analise.silo_tipo || 'silo';
            const temConfig = config.length === cabosMapa.length;

            // Forma da estrutura (corpo, telhado e altura do teto em cada x) e colunas de cabos
            let forma;
            let colunas;
            if (tipo === 'armazem' && this.fileirasArmazem) {
                const hL = ARMAZEM.largura / 2;
                const yBeiral = ARMAZEM.yPiso + ARMAZEM.altura;
                forma = {
                    xMin: -hL, xMax: hL, yBase: ARMAZEM.yPiso, yBeiral, beiral: 0.1,
                    topo: yBeiral + ARMAZEM.telhadoAltura, tetoEm: tetoArmazem, aneis: false,
                };
                const daFileira = config.filter(c => c.arco === this.fileiraAtual.arco);
                colunas = geometriaArmazem(daFileira).map(g => ({ cabo: g.cabo, x: g.x, alturas: g.alturas, topo: g.topoCabo }));
            } else if (tipo === 'silo' && temConfig) {
                forma = {
                    xMin: -R, xMax: R, yBase: Y_BASE, yBeiral: Y_BEIRAL, beiral: R_TELHADO - R,
                    topo: Y_BEIRAL + TELHADO_H, tetoEm: x => Y_BEIRAL + TELHADO_H * (1 - Math.min(Math.abs(x), R_TELHADO) / R_TELHADO), aneis: true,
                };
                // Vista esquemática: arco externo junto às paredes, internos em direção ao
                // centro, cabo central no meio; alturas reais de cada arco.
                const geo = geometriaCabos(config);
                const externos = [...new Set(geo.filter(g => g.arco !== 999).map(g => g.arco))]
                    .map(arco => geo.filter(g => g.arco === arco).sort((a, b) => a.angulo - b.angulo))
                    .sort((a, b) => b[0].raio - a[0].raio);
                const esquerda = [];
                const direita = [];
                externos.forEach(cabosArco => {
                    const metade = Math.ceil(cabosArco.length / 2);
                    esquerda.push(...cabosArco.slice(0, metade));
                    direita.unshift(...cabosArco.slice(metade).reverse());
                });
                const ordem = [...esquerda, ...geo.filter(g => g.arco === 999), ...direita];
                const larguraUtil = Math.min(R * 0.88, Math.max(...geo.map(g => g.raio)) + 0.35);
                const passo = ordem.length > 1 ? (2 * larguraUtil) / (ordem.length - 1) : 1;
                colunas = ordem.map((g, i) => ({ cabo: g.cabo, x: ordem.length > 1 ? -larguraUtil + passo * i : 0, alturas: g.alturas, topo: g.topoCabo }));
            } else {
                // Sem configuração de cabos: cabos lado a lado em uma forma genérica
                const telhadoH = TELHADO_H * 0.5;
                forma = {
                    xMin: -R, xMax: R, yBase: Y_BASE, yBeiral: Y_BEIRAL, beiral: 0,
                    topo: Y_BEIRAL + telhadoH, tetoEm: x => Y_BEIRAL + telhadoH * (1 - Math.min(Math.abs(x), R) / R), aneis: false,
                };
                const n = cabosMapa.length;
                colunas = cabosMapa.map((c, i) => {
                    const total = Math.max(1, c.total_sensores);
                    const y0 = Y_BASE + 0.12;
                    const y1 = Y_BEIRAL - 0.1;
                    return {
                        cabo: String(c.cabo),
                        x: n > 1 ? -R * 0.88 + (1.76 * R * i) / (n - 1) : 0,
                        alturas: Array.from({ length: total }, (_, j) => (total > 1 ? y0 + ((y1 - y0) / (total - 1)) * j : (y0 + y1) / 2)),
                        topo: Y_BEIRAL - 0.02,
                    };
                });
            }

            // Escala dos traços: o armazém é mais largo que o silo na cena
            const k = (forma.xMax - forma.xMin + 2 * forma.beiral) / (2 * R_TELHADO);
            colunas.forEach(c => { c.topo = Math.min(c.topo, forma.tetoEm(c.x) - 0.05 * k); });
            const xs = colunas.map(c => c.x).sort((a, b) => a - b);
            const passo = xs.length > 1 ? Math.min(...xs.slice(1).map((x, i) => x - xs[i])) : k;
            const sepY = Math.min(...colunas.map(c => (c.alturas.length > 1 ? c.alturas[1] - c.alturas[0] : k)));
            const raio = Math.max(0.03 * k, Math.min(0.075 * k, passo * 0.36, sepY * 0.4));

            const cores = this.dark
                ? { corpo: '#2f353d', telhado: '#3a414b', anel: '#3a414b', cabo: '#64748b' }
                : { corpo: '#e5e7eb', telhado: '#d1d5db', anel: '#d4d8de', cabo: '#9ca3af' };

            const d = this.dominioTemp;
            const pontos = [];
            const topos = [];
            colunas.forEach(c => {
                const dados = porCabo[c.cabo];
                let topo = null;
                c.alturas.forEach((yReal, j) => {
                    const y = Math.min(yReal, c.topo - raio);
                    const s = dados.sensores.find(x => x.sensor === j + 1);
                    if (!s) return;
                    if (s.submerso) topo = y;
                    pontos.push({
                        key: `${c.cabo}-${s.sensor}`, x: c.x, y: -y, off: s.off,
                        cor: s.off ? null : corEscala(this.escalaCorte, d ? (s.temperatura - d.min) / (d.max - d.min) : 0.5).background,
                        destaque: !s.off && (s.foco || s.aquecendo),
                        titulo: `Cabo ${c.cabo} · S${s.sensor}` + (s.off ? ' · OFF' : ` · ${this.fmt(s.temperatura)} °C`) + (s.submerso ? ' · na massa' : ''),
                    });
                });
                topos.push(topo);
            });

            // Superfície do grão: topo da massa em cada cabo, suavizado entre vizinhos
            let grao = null;
            let superficie = null;
            if (topos.some(t => t != null)) {
                const ordem = colunas.map((c, i) => ({ x: c.x, y: topos[i] == null ? forma.yBase : topos[i] + raio * 1.4 })).sort((a, b) => a.x - b.x);
                const n = ordem.length;
                const suave = ordem.map((o, i) => 0.25 * ordem[Math.max(0, i - 1)].y + 0.5 * o.y + 0.25 * ordem[Math.min(n - 1, i + 1)].y);
                const pts = [[forma.xMin, suave[0]], ...ordem.map((o, i) => [o.x, suave[i]]), [forma.xMax, suave[n - 1]]]
                    .map(([x, y]) => [x, Math.min(Math.max(y, forma.yBase), forma.tetoEm(x) - 0.03 * k)]);
                superficie = caminhoSuave(pts);
                grao = `${superficie} L ${forma.xMax} ${-forma.yBase} L ${forma.xMin} ${-forma.yBase} Z`;
            }

            const fonte = 0.085 * k;
            const mostrarRotulos = passo >= fonte * 1.7;
            const margem = 0.08 * k;
            const xEsq = forma.xMin - forma.beiral - margem;
            const largura = forma.xMax - forma.xMin + 2 * (forma.beiral + margem);
            const yMin = forma.yBase - (mostrarRotulos ? 0.2 * k : 0.06 * k);
            const alturaCena = forma.topo + margem - yMin;
            const aneis = [];
            if (forma.aneis) for (let y = forma.yBase + 0.25; y < forma.yBeiral - 0.05; y += 0.25) aneis.push(-y);
            return {
                viewBox: `${xEsq.toFixed(3)} ${(-(forma.topo + margem)).toFixed(3)} ${largura.toFixed(3)} ${alturaCena.toFixed(3)}`,
                altura: Math.round((340 * alturaCena) / largura),
                corpo: { x: forma.xMin, y: -forma.yBeiral, w: forma.xMax - forma.xMin, h: forma.yBeiral - forma.yBase },
                telhado: [[forma.xMin - forma.beiral, forma.yBeiral], [0, forma.topo], [forma.xMax + forma.beiral, forma.yBeiral]].map(([x, y]) => `${x},${-y}`).join(' '),
                aneis,
                cores,
                grao,
                superficie,
                raio,
                fonte,
                traco: { anel: 0.012 * k, superficie: 0.022 * k, cabo: 0.014 * k },
                pontos,
                linhas: colunas.map(c => ({ cabo: c.cabo, x: c.x, y1: -forma.yBase, y2: -c.topo })),
                rotulos: mostrarRotulos ? colunas.map(c => ({ cabo: c.cabo, x: c.x, y: -(forma.yBase - 0.13 * k) })) : [],
            };
        },

        acoesView() {
            return (this.rel.acoes || []).map((a, i) => {
                const p = PRIORIDADE[Math.min(i, PRIORIDADE.length - 1)];
                return { ...a, n: String(i + 1).padStart(2, '0'), tag: CATEGORIAS[a.categoria] || 'Ação', cor: p.cor, fundo: p.fundo };
            });
        },

        riscosView() {
            return (this.rel.riscos || []).map(r => {
                const n = NIVEIS[r.nivel] || NIVEIS.indeterminado;
                return { ...r, nivelTexto: n.texto, cor: n.cor, fundo: n.fundo };
            });
        },
        reguas() {
            const rec = this.rec;
            const at = this.aer.atual;
            const agora = this.aer.agora;
            const regua = (nome, corTexto, chave, min, max, un, valorAgora) => {
                const lo = rec[`${chave}_minima`];
                const hi = rec[`${chave}_maxima`];
                const usaAtual = at && !(at.eg && chave === 'umidade');
                const atLo = usaAtual ? at[`${chave}_minima`] : null;
                const atHi = usaAtual ? at[`${chave}_maxima`] : null;
                const vals = [lo, hi, atLo, atHi, valorAgora].filter(v => v != null);
                const mn = Math.min(min, ...vals);
                const mx = Math.max(max, ...vals);
                const p = v => `${((v - mn) / (mx - mn)) * 100}%`;
                const faixa = (a, b) => ({ left: p(a), width: `calc(${p(b)} - ${p(a)})` });
                return {
                    nome, corTexto,
                    min: `${mn}${un}`, max: `${mx}${un}`,
                    rec: `${lo}–${hi}${un}`,
                    atual: atLo != null && atHi != null ? `${atLo}–${atHi}${un}` : null,
                    atualDifere: atLo != null && (atLo !== lo || atHi !== hi),
                    recEstilo: faixa(lo, hi),
                    atualEstilo: atLo != null && atHi != null ? { ...faixa(atLo, atHi), background: this.hachuraAtual } : null,
                    // Pixel inteiro: com left em % a marca cai em fração de pixel e o
                    // navegador a desenha ora com 2, ora com 3 px de largura.
                    agoraEstilo: valorAgora != null ? this.estiloMarca((valorAgora - mn) / (mx - mn), p(valorAgora)) : null,
                    agora: valorAgora != null ? `${this.fmt(valorAgora, 0)}${un}` : null,
                };
            };
            const lista = [];
            if (rec.temperatura_minima != null) {
                lista.push(regua('Temperatura do ar', 'tw-text-orange-400', 'temperatura', 0, 40, '°', agora ? agora.temperatura : null));
            }
            lista.push(regua('Umidade relativa do ar', 'tw-text-blue-400', 'umidade', 0, 100, '%', agora ? agora.umidade : null));
            return lista;
        },

        linhasMapa() {
            const cabos = this.mapa.cabos || [];
            const max = Math.max(0, ...cabos.map(c => c.total_sensores));
            const topo = {};
            cabos.forEach(c => {
                const sub = c.sensores.filter(s => s.submerso).map(s => s.sensor);
                topo[c.cabo] = c.topo_massa ?? (sub.length ? Math.max(...sub) : 0);
            });
            const topos = [...new Set(Object.values(topo))];
            const nivelUnico = this.mapa.origem_nivel !== 'deteccao' && topos.length === 1 && topos[0] > 0 && topos[0] < max ? topos[0] : null;
            const linhas = [];
            for (let s = max; s >= 1; s--) {
                if (s === nivelUnico) linhas.push({ key: 'nivel', nivel: true });
                linhas.push({
                    key: 'l' + s,
                    sensor: s,
                    celulas: cabos.map(c => this.celula(c, s, c.sensores.find(x => x.sensor === s), !nivelUnico && topo[c.cabo] === s && s < c.total_sensores)),
                });
            }
            return linhas;
        },

        tempsMassa() {
            const temps = [];
            (this.mapa.cabos || []).forEach(c => c.sensores.forEach(s => {
                if (s.submerso && s.temperatura != null) temps.push(s.temperatura);
            }));
            return temps;
        },

        // Escala centrada na média da massa, com no mínimo ±2 °C, para que uma massa
        // uniforme apareça neutra e só os desvios ganhem cor.
        dominioTemp() {
            let temps = this.tempsMassa;
            if (!temps.length) {
                temps = [];
                (this.mapa.cabos || []).forEach(c => c.sensores.forEach(s => { if (!s.off && s.temperatura != null) temps.push(s.temperatura); }));
            }
            if (!temps.length) return null;
            const media = temps.reduce((a, b) => a + b, 0) / temps.length;
            const desvio = Math.max(2, ...temps.map(t => Math.abs(t - media)));
            return { min: media - desvio, max: media + desvio };
        },

        massaTodaAcimaIdeal() {
            const max = this.ind.faixa_ideal && this.ind.faixa_ideal.max;
            return max != null && this.tempsMassa.length > 0 && this.tempsMassa.every(t => t > max);
        },

        alturaCelula() {
            const max = Math.max(0, ...(this.mapa.cabos || []).map(c => c.total_sensores));
            return max > 10 ? 'tw-h-9' : 'tw-h-11';
        },

        legendaEscala() {
            if (this.modoMapa === 'delta') {
                return { min: '0', max: '+3 °C', gradiente: gradienteCss(ESCALA_DELTA) };
            }
            if (!this.dominioTemp) return null;
            return {
                min: this.fmt(this.dominioTemp.min),
                max: this.fmt(this.dominioTemp.max) + ' °C',
                gradiente: gradienteCss(ESCALA_TEMP),
            };
        },

        explicacaoMassa() {
            const origem = this.mapa.origem_nivel;
            if (origem === 'deteccao') {
                const data = this.mapa.data_deteccao ? ` de ${this.mapa.data_deteccao.split('-').reverse().join('/')}` : '';
                return `Sensores na massa detectados pelas leituras das 04h e 16h${data}: quem variou até 3 °C está no grão. A linha tracejada marca o topo detectado em cada cabo.`;
            }
            if (origem === 'manual') {
                return 'Massa definida pelo nível informado manualmente, aplicado à altura de cada cabo. É uma aproximação: o grão pode não estar nivelado.';
            }
            return 'Sem leituras de 04h e 16h para detectar a massa: posição estimada pelo nível do silo, aplicada à altura de cada cabo.';
        },

        legenda() {
            const itens = [
                { texto: 'Topo da massa', estilo: { borderTop: '2px dashed #4ade80', borderRadius: 0, height: '0.5rem', marginTop: '0.5rem' } },
                { texto: 'Sensor OFF', estilo: this.hachura },
                { texto: 'Aquecendo / foco', estilo: { boxShadow: `0 0 0 2px ${this.dark ? '#1a1d21' : '#fff'}, 0 0 0 4px #f97316` } },
            ];
            if (this.modoMapa === 'temp' && !this.massaTodaAcimaIdeal && this.ind.faixa_ideal && this.ind.faixa_ideal.max != null) {
                itens.push({ texto: `Acima do ideal (${this.ind.faixa_ideal.max} °C)`, classe: 'tw-w-2 tw-h-2 tw-rounded-full tw-bg-red-600' });
            }
            return itens;
        },

        estiloAcima() {
            return this.dark ? { background: '#22262b', color: '#6b7280' } : { background: '#f3f4f6', color: '#9ca3af' };
        },

        hachura() {
            return this.dark
                ? { background: 'repeating-linear-gradient(135deg, #2a2e35 0 5px, #1d2025 5px 10px)' }
                : { background: 'repeating-linear-gradient(135deg, #e5e7eb 0 5px, #f3f4f6 5px 10px)' };
        },

    },
    watch: {
        'analise.id'() {
            this.fileira = null;
        },
    },
    mounted() {
        this.observarRegua();
    },
    updated() {
        this.observarRegua();
    },
    beforeUnmount() {
        if (this.reguaObserver) this.reguaObserver.disconnect();
        cancelAnimationFrame(this.quadroRegua);
    },
    methods: {
        // Posição e largura da marca alinhadas aos pixels físicos da tela (considera
        // zoom e escala do sistema), para as marcas terem sempre a mesma espessura.
        estiloMarca(fracao, percentual) {
            if (!this.larguraRegua) return { left: `calc(${percentual} - 1px)` };
            const dpr = window.devicePixelRatio || 1;
            const larguraDev = Math.max(2, Math.round(3 * dpr));
            const centroDev = Math.round(this.larguraRegua * fracao * dpr);
            return {
                left: `${(centroDev - Math.floor(larguraDev / 2)) / dpr}px`,
                width: `${larguraDev / dpr}px`,
            };
        },
        trocarFileira(passo) {
            const lista = this.fileirasArmazem;
            const i = Math.min(lista.length - 1, Math.max(0, this.indiceFileira + passo));
            this.fileira = lista[i].arco;
        },
        observarRegua() {
            const ref = this.$refs.reguaBarra;
            const el = Array.isArray(ref) ? ref[0] : ref;
            if (!el || el === this.reguaObservada || typeof ResizeObserver === 'undefined') return;
            if (this.reguaObserver) this.reguaObserver.disconnect();
            this.reguaObservada = el;
            // Atualiza no próximo quadro para não alterar o layout dentro do callback
            this.reguaObserver = new ResizeObserver(([entrada]) => {
                const largura = Math.round(entrada.contentRect.width);
                if (largura === this.larguraRegua) return;
                cancelAnimationFrame(this.quadroRegua);
                this.quadroRegua = requestAnimationFrame(() => { this.larguraRegua = largura; });
            });
            this.reguaObserver.observe(el);
        },
        fmt(v, casas = 1) {
            if (v === null || v === undefined || v === '' || isNaN(Number(v))) return '—';
            return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
        },
        sinal(v) {
            return (v > 0 ? '+' : '') + this.fmt(v);
        },
        barra(h) {
            const max = Math.max(1, ...this.horas.por_dia.map(d => d.horas));
            return `${Math.max(4, Math.round((h / max) * 64))}px`;
        },
        abaClass(modo) {
            if (this.modoMapa === modo) return this.dark ? 'tw-bg-gray-800 tw-text-green-400' : 'tw-bg-white tw-text-green-700 tw-shadow';
            return this.muted;
        },
        celula(cabo, linha, s, topoSubmerso) {
            const key = `${cabo.cabo}-${linha}`;
            if (!s) return { key, texto: '', classe: '', estilo: {}, titulo: '' };
            const titulo = `Cabo ${cabo.cabo} · Sensor ${s.sensor}` + (s.off ? ' · sem leitura' : (s.submerso ? ' · na massa' : ' · fora da massa'))
                + (s.delta_7d != null ? ` · 7 dias: ${this.sinal(s.delta_7d)} °C` : '');
            const topo = topoSubmerso;
            if (s.off) {
                return { key, texto: 'OFF', classe: 'tw-text-[10px]', estilo: { ...this.hachura, color: '#6b7280' }, titulo, topo };
            }
            const texto = this.modoMapa === 'delta' ? (s.delta_7d != null ? this.sinal(s.delta_7d) : '—') : this.fmt(s.temperatura);
            let estilo;
            if (this.modoMapa === 'delta') {
                estilo = s.delta_7d != null ? corEscala(ESCALA_DELTA, Math.max(0, s.delta_7d) / 3) : { ...this.estiloAcima };
            } else {
                const d = this.dominioTemp;
                estilo = corEscala(ESCALA_TEMP, d ? (s.temperatura - d.min) / (d.max - d.min) : 0.5);
            }
            if (s.foco || s.aquecendo) {
                estilo.boxShadow = `0 0 0 2px ${this.dark ? '#1a1d21' : '#fff'}, 0 0 0 4px #f97316`;
                estilo.fontWeight = 700;
            }
            const max = this.ind.faixa_ideal && this.ind.faixa_ideal.max;
            const acimaIdeal = this.modoMapa === 'temp' && !this.massaTodaAcimaIdeal && s.submerso && max != null && s.temperatura > max;
            return { key, texto, classe: '', estilo, titulo, acimaIdeal, topo };
        },
    },
};
</script>
